import React from 'react';
import DialogLayout from 'layouts/DialogLayout';
import { FileText, Sheet, X } from 'lucide-react';
import { TransactionsFilters } from 'utils/types';
import { formatDateForInput } from 'utils/Functions';

interface ReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  filters: TransactionsFilters;
  setFilters: (filters: TransactionsFilters) => void;
  onDownloadCSV: () => void;
  onDownloadPDF: () => void;
  isPendingCSV: boolean;
  isPendingPDF: boolean;
  errors: { start: boolean; end: boolean };
}

export const ReportDialog: React.FC<ReportDialogProps> = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  onDownloadCSV,
  onDownloadPDF,
  isPendingCSV,
  isPendingPDF,
  errors,
}) => (
  <DialogLayout isOpen={isOpen} onClose={onClose} height="auto">
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            Generate report
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Select filters and export format
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-6 py-5 space-y-5">
        {/* Date range — side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              From
            </label>
            <input
              type="datetime-local"
              value={formatDateForInput(filters.fromDate)}
              onChange={(e) =>
                setFilters({ ...filters, fromDate: new Date(e.target.value) })
              }
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                errors.start
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                  : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.start && (
              <p className="text-xs text-red-500 mt-1">Required</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              To
            </label>
            <input
              type="datetime-local"
              value={formatDateForInput(filters.toDate)}
              onChange={(e) =>
                setFilters({ ...filters, toDate: new Date(e.target.value) })
              }
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                errors.end
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                  : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.end && (
              <p className="text-xs text-red-500 mt-1">Required</p>
            )}
          </div>
        </div>

        {/* Payment channel tiles */}
        {/* <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">
            Payment channel
          </label>
          <div className="grid grid-cols-2 gap-2">
            {CHANNEL_OPTIONS.map((opt) => {
              const isActive = filters.chanel === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() =>
                    setFilters({
                      ...filters,
                      chanel: isActive ? '' : opt.value,
                    })
                  }
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    isActive
                      ? 'border-blue-300 bg-blue-50 text-blue-800'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: isActive ? opt.color : '#9ca3af' }}
                  />
                  {opt.label}
                  {isActive && (
                    <span className="ml-auto text-blue-500">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M2 6l3 3 5-5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {filters.chanel
              ? `Filtering by: ${CHANNEL_OPTIONS.find((o) => o.value === filters.chanel)?.label}`
              : 'No channel selected — all channels will be included'}
          </p>
        </div> */}
      </div>

      {/* Footer */}
      <div className="px-6 pb-6 pt-2 flex items-center justify-between gap-3 border-t border-gray-100">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <div className="flex gap-2">
          <button
            onClick={onDownloadCSV}
            disabled={isPendingCSV}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPendingCSV ? (
              <svg
                className="animate-spin h-3.5 w-3.5 text-gray-500"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <Sheet className="h-3.5 w-3.5" />
            )}
            CSV
          </button>
          <button
            onClick={onDownloadPDF}
            disabled={isPendingPDF}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPendingPDF ? (
              <svg
                className="animate-spin h-3.5 w-3.5 text-white"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              <FileText className="h-3.5 w-3.5" />
            )}
            PDF
          </button>
        </div>
      </div>
    </div>
  </DialogLayout>
);
